import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronDown, ChevronUp, CheckCircle2, Rocket } from 'lucide-react';
import { Button } from './button';
import { BelongingToolsIcon, ConsentFlowIcon, MairyIcon, FederationIcon } from './legendary-icons';
import { BelongingTools } from './belonging-tools';
import { ConsentFlow } from './consent-flow';
import { MaryChat } from './mary-chat';
import { FederationView } from './federation-view';
import { RemindersPanel } from './reminders-panel';
import { useNavigate } from 'react-router-dom';

// Reminders icon component
const RemindersIcon = ({ size = 24, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 6v6l4 2" strokeLinecap="round" />
    <path d="M8 2l1 2M16 2l-1 2" />
  </svg>
);

// Startup icon component
const StartupIcon = ({ size = 24, className = '' }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M12 2l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9z" />
  </svg>
);

const TOOLS = [
  {
    key: 'mairy',
    name: 'M.ai.ry',
    icon: MairyIcon,
    color: '#4a7c59',
    description: 'AI guide & pattern detector'
  },
  {
    key: 'belonging',
    name: 'Belonging',
    icon: BelongingToolsIcon,
    color: '#2f6f3e',
    description: 'ElchmE framework & repair flows'
  },
  {
    key: 'decisions',
    name: 'Decisions',
    icon: ConsentFlowIcon,
    color: '#b9935a',
    description: 'Consent-based decision engine'
  },
  {
    key: 'reminders',
    name: 'Tasks',
    icon: RemindersIcon,
    color: '#3B82F6',
    description: 'Tasks & reminders'
  },
  {
    key: 'federation',
    name: 'Federation',
    icon: FederationIcon,
    color: '#2b6b69',
    description: 'Planetary network view',
    beta: true
  }
];

export const ToolsPanel = ({
  projectId,
  sectionKey,
  subElementKey,
  context,
  onInsert,
  isCollapsed = false,
  onToggleCollapse,
  reminders = [],
  onAddReminder,
  onToggleReminder,
  onDeleteReminder,
  showStartupLink = true,
  theme = 'earth', // 'earth' for community, 'dark' for startup
  studioType = 'community' // 'community' or 'startup' - tells M.ai.ry which context to use
}) => {
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState('mairy');
  const [isMinimized, setIsMinimized] = useState(false);

  const renderToolContent = () => {
    switch (activeTool) {
      case 'belonging':
        return <BelongingTools projectId={projectId} />;
      case 'decisions':
        return <ConsentFlow projectId={projectId} />;
      case 'mairy':
        return (
          <MaryChat
            projectId={projectId}
            sectionKey={sectionKey}
            subElementKey={subElementKey}
            context={context}
            onInsert={onInsert}
            studioType={studioType}
          />
        );
      case 'federation':
        return <FederationView projectId={projectId} />;
      case 'reminders':
        return (
          <div className="p-3 h-full overflow-auto">
            <RemindersPanel
              reminders={reminders}
              onAddReminder={onAddReminder}
              onToggleComplete={onToggleReminder}
              onDeleteReminder={onDeleteReminder}
              theme={theme}
              className="h-full"
            />
          </div>
        );
      default:
        return null;
    }
  };

  if (isCollapsed) {
    return (
      <div className="flex flex-col gap-2" data-testid="tools-panel-collapsed">
        {TOOLS.map((tool) => (
          <Button
            key={tool.key}
            variant="ghost"
            size="sm"
            onClick={() => {
              setActiveTool(tool.key);
              onToggleCollapse?.();
            }}
            className={`w-12 h-12 p-0 rounded-xl transition-all duration-200 relative ${
              activeTool === tool.key
                ? 'bg-[var(--primary)]/20 border-[var(--primary)]/40'
                : 'hover:bg-[var(--muted)]'
            }`}
            style={{
              borderColor: activeTool === tool.key ? tool.color : 'transparent',
              borderWidth: '2px'
            }}
            data-testid={`tool-btn-${tool.key}`}
          >
            <tool.icon size={24} className={activeTool === tool.key ? 'text-[var(--primary)]' : 'text-[var(--fg)]/60'} />
            {tool.beta && (
              <span className="absolute -top-1 -right-1 bg-[var(--accent)] text-white text-[8px] px-1 py-0.5 rounded-full font-bold">
                β
              </span>
            )}
          </Button>
        ))}
        
        {/* Startup Studio Link */}
        {showStartupLink && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/startup')}
            className="w-12 h-12 p-0 rounded-xl transition-all duration-200 hover:bg-indigo-500/20 border-2 border-transparent hover:border-indigo-500/40"
            data-testid="startup-link-collapsed"
          >
            <Rocket size={20} className="text-indigo-400" />
          </Button>
        )}
      </div>
    );
  }

  const cardBg = theme === 'dark' ? 'bg-white/5 backdrop-blur border-white/10' : 'bg-[var(--card)] border-[var(--muted)]';
  const textColor = theme === 'dark' ? 'text-white' : 'text-[var(--fg)]';

  return (
    <div
      className={`${cardBg} rounded-2xl border shadow-lg overflow-hidden flex flex-col`}
      style={{ height: isMinimized ? 'auto' : '100%', maxHeight: 'calc(100vh - 140px)' }}
      data-testid="tools-panel"
    >
      {/* Header */}
      <div className={`border-b ${theme === 'dark' ? 'border-white/10' : 'border-[var(--muted)]/50'} p-3`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className={`font-heading text-sm font-semibold ${textColor}`}>
            Tools Panel
          </h3>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="w-7 h-7 p-0"
            >
              {isMinimized ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
                className="w-7 h-7 p-0"
              >
                <X size={16} />
              </Button>
            )}
          </div>
        </div>

        {/* Tool Tabs */}
        <div className={`flex gap-1 p-1 ${theme === 'dark' ? 'bg-white/5' : 'bg-[var(--muted)]/30'} rounded-xl`}>
          {TOOLS.map((tool) => (
            <button
              key={tool.key}
              onClick={() => setActiveTool(tool.key)}
              className={`flex-1 flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg transition-all duration-200 relative ${
                activeTool === tool.key
                  ? theme === 'dark' ? 'bg-white/10 shadow-sm' : 'bg-[var(--card)] shadow-sm'
                  : theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-[var(--card)]/50'
              }`}
              data-testid={`tool-tab-${tool.key}`}
            >
              <tool.icon
                size={16}
                className={activeTool === tool.key 
                  ? theme === 'dark' ? 'text-white' : 'text-[var(--primary)]' 
                  : theme === 'dark' ? 'text-white/50' : 'text-[var(--fg)]/50'}
              />
              <span
                className={`text-[9px] font-medium ${
                  activeTool === tool.key 
                    ? theme === 'dark' ? 'text-white' : 'text-[var(--primary)]' 
                    : theme === 'dark' ? 'text-white/50' : 'text-[var(--fg)]/50'
                }`}
              >
                {tool.name}
              </span>
              {tool.beta && (
                <span className="absolute -top-1 -right-0.5 bg-[var(--accent)] text-white text-[7px] px-1 py-0.5 rounded-full font-bold">
                  β
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Startup Studio Quick Link */}
        {showStartupLink && (
          <button
            onClick={() => navigate('/startup')}
            className={`mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all ${
              theme === 'dark' 
                ? 'bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30' 
                : 'bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20'
            }`}
          >
            <Rocket size={14} />
            <span className="text-xs font-medium">Startup Studio</span>
          </button>
        )}
      </div>

      {/* Tool Content */}
      <AnimatePresence mode="wait">
        {!isMinimized && (
          <motion.div
            key={activeTool}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="flex-1 overflow-hidden"
          >
            {renderToolContent()}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ToolsPanel;
